import React, { useMemo, useState } from 'react';
import { diffText } from '../utils/textDiff';
import { formatDateTime } from '../utils/format';
import './QualityReviewPanel.css';

export type QualityIssueType = 'ambiguous' | 'english' | 'typo' | 'url' | 'keyword' | 'category' | 'other';

export interface QualityReview {
  status: 'needs_review' | 'clean' | 'resolved';
  checked_at: string;
  model?: string;
  summary?: string;
  issues: Array<{ type: QualityIssueType; field: string; severity: 'high' | 'low'; message: string }>;
  suggestion?: {
    question_text?: string;
    options?: Array<{ option_number: number; option_text: string; explanation?: string }>;
    explanation?: string;
    reasoning?: string;
    references?: Array<{ url: string; title?: string }>;
    keywords?: string[];
    domain?: string;
    difficulty?: 1 | 2 | 3;
  };
  changes?: Array<{ field: string; reason: string }>;
  url_checks?: Array<{ url: string; ok: boolean; unverified?: boolean; status?: number; title?: string; final_url?: string; error?: string }>;
  resolution?: 'applied' | 'dismissed';
  resolved_by?: string;
  resolved_at?: string;
}

interface QuestionContent {
  question_text: string;
  options: Array<{ option_number: number; option_text: string; explanation?: string }>;
  explanation: string;
  reasoning: string;
  references?: Array<{ url: string; title?: string }>;
  correct_answers?: number[];
  keywords?: string[];
  domain?: string | null;
  difficulty?: 1 | 2 | 3 | null;
  quality_review?: QualityReview;
}

type SuggestField = 'question_text' | 'options' | 'explanation' | 'reasoning' | 'references' | 'keywords' | 'domain' | 'difficulty';

const DIFFICULTY_NAMES: Record<number, string> = { 1: '쉬움', 2: '보통', 3: '어려움' };

export const QUALITY_ISSUE_LABELS: Record<QualityIssueType, string> = {
  ambiguous: '의도 불명확',
  english: '영어 표기',
  typo: '오타·탈자',
  url: 'URL 문제',
  keyword: '키워드',
  category: '영역·난이도',
  other: '기타',
};

const FIELD_LABELS: Record<string, string> = {
  question_text: '문제',
  options: '선택지 · 보기별 해설',
  explanation: '공통 해설',
  reasoning: '구 형식 근거',
  references: '참고 자료',
  keywords: '키워드',
  domain: '출제 영역',
  difficulty: '난이도',
  url: 'URL',
};

const fieldLabel = (field: string) => {
  if (field.startsWith('option:')) return `선택지 ${field.split(':')[1]}번`;
  return FIELD_LABELS[field] || field;
};

/** 원본과 수정안의 달라진 부분을 색으로 표시합니다. */
const DiffView: React.FC<{ before: string; after: string }> = ({ before, after }) => {
  const segments = useMemo(() => diffText(before, after), [before, after]);
  return (
    <div className="quality-diff">
      <div className="quality-diff__side">
        <span className="quality-diff__label">지금</span>
        <p>
          {segments.filter((segment) => segment.type !== 'added').map((segment, index) => (
            segment.type === 'removed'
              ? <del key={index}>{segment.text}</del>
              : <span key={index}>{segment.text}</span>
          ))}
        </p>
      </div>
      <div className="quality-diff__side quality-diff__side--after">
        <span className="quality-diff__label">수정안</span>
        <p>
          {segments.filter((segment) => segment.type !== 'removed').map((segment, index) => (
            segment.type === 'added'
              ? <ins key={index}>{segment.text}</ins>
              : <span key={index}>{segment.text}</span>
          ))}
        </p>
      </div>
    </div>
  );
};

interface Props {
  question: QuestionContent;
  /** 출제 영역 id → 이름 표시용 */
  domains?: Array<{ id: string; name: string }>;
  busy?: boolean;
  onApply: (fields: SuggestField[]) => void;
  onDismiss: () => void;
  onRecheck?: () => void;
}

/** 문제 수정 화면 상단: AI 품질 검토 결과와 수정안 비교, 적용/완료 */
const QualityReviewPanel: React.FC<Props> = ({ question, domains = [], busy = false, onApply, onDismiss, onRecheck }) => {
  const domainName = (id?: string | null) => (id ? domains.find((domain) => domain.id === id)?.name || id : '미분류');
  const review = question.quality_review;
  const suggestion = review?.suggestion || {};
  const suggestedFields = (['question_text', 'options', 'explanation', 'reasoning', 'references', 'domain', 'difficulty', 'keywords'] as SuggestField[])
    .filter((field) => suggestion[field] !== undefined);
  const [selected, setSelected] = useState<Set<SuggestField>>(() => new Set(suggestedFields));

  if (!review) return null;

  if (review.status !== 'needs_review') {
    return (
      <div className="quality-panel quality-panel--done">
        <span>
          AI 품질 검토 {formatDateTime(review.checked_at)} ·{' '}
          {review.status === 'clean'
            ? '문제 없음'
            : `${review.resolution === 'applied' ? '수정안 적용' : '이상 없음 확인'}으로 완료 (${review.resolved_by || '관리자'}, ${formatDateTime(review.resolved_at)})`}
        </span>
        {onRecheck && <button type="button" className="admin-btn sm secondary" onClick={onRecheck} disabled={busy}>AI 다시 검토</button>}
      </div>
    );
  }

  const reasonFor = (field: SuggestField) =>
    (review.changes || []).filter((change) => change.field === field || (field === 'options' && change.field.startsWith('option'))).map((change) => change.reason);
  const toggle = (field: SuggestField) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(field)) next.delete(field);
    else next.add(field);
    return next;
  });
  const urlNotes = (review.changes || []).filter((change) => change.field === 'url');
  const highCount = review.issues.filter((issue) => issue.severity === 'high').length;

  return (
    <section className="quality-panel" aria-label="AI 품질 검토 결과">
      <header className="quality-panel__head">
        <div>
          <h4>AI 품질 검토: 사람 확인 필요</h4>
          <p>
            {formatDateTime(review.checked_at)} · {review.model || 'AI'}
            {highCount > 0 && <strong className="quality-panel__high"> · 학생이 풀기 어려운 문제 {highCount}건</strong>}
          </p>
        </div>
        {onRecheck && <button type="button" className="admin-btn sm secondary" onClick={onRecheck} disabled={busy}>AI 다시 검토</button>}
      </header>

      {review.summary && <p className="quality-panel__summary">{review.summary}</p>}

      <ul className="quality-panel__issues">
        {review.issues.map((issue, index) => (
          <li key={`${issue.type}-${issue.field}-${index}`}>
            <span className={`quality-panel__tag quality-panel__tag--${issue.severity}`}>{QUALITY_ISSUE_LABELS[issue.type] || issue.type}</span>
            <span className="quality-panel__field">{fieldLabel(issue.field)}</span>
            <span>{issue.message}</span>
          </li>
        ))}
      </ul>

      {suggestedFields.length > 0 && (
        <div className="quality-panel__changes">
          {suggestedFields.map((field) => (
            <div key={field} className="quality-panel__change">
              <label className="quality-panel__change-head">
                <input type="checkbox" checked={selected.has(field)} onChange={() => toggle(field)} disabled={busy} />
                <strong>{FIELD_LABELS[field]}</strong>
                <span>{selected.has(field) ? '적용' : '적용 안 함'}</span>
              </label>
              {reasonFor(field).map((reason, index) => <p key={index} className="quality-panel__reason">{reason}</p>)}
              {field === 'options' && (suggestion.options || []).map((option) => {
                const original = question.options.find((item) => item.option_number === option.option_number);
                const textChanged = (original?.option_text || '') !== option.option_text;
                const explanationChanged = (original?.explanation || '').trim() !== (option.explanation || '').trim();
                if (!textChanged && !explanationChanged) return null;
                const isCorrect = question.correct_answers?.includes(option.option_number);
                return (
                  <div key={option.option_number} className="quality-panel__option">
                    <span className="quality-panel__option-no">{option.option_number}</span>
                    <div className="quality-panel__option-diffs">
                      {textChanged && <DiffView before={original?.option_text || ''} after={option.option_text} />}
                      {explanationChanged && (
                        <>
                          <span className="quality-panel__sub">보기 해설{isCorrect !== undefined ? ` (${isCorrect ? '정답' : '오답'})` : ''}</span>
                          <DiffView before={original?.explanation || ''} after={option.explanation || ''} />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {field === 'references' && (
                <ul className="quality-panel__refs">
                  {(suggestion.references || []).map((reference) => (
                    <li key={reference.url} className={(question.references || []).some((item) => item.url === reference.url) ? '' : 'is-added'}>
                      {(question.references || []).some((item) => item.url === reference.url) ? '유지' : '추가'} · {reference.title ? `${reference.title} — ` : ''}{reference.url}
                    </li>
                  ))}
                  {(question.references || []).filter((item) => !(suggestion.references || []).some((reference) => reference.url === item.url)).map((reference) => (
                    <li key={`removed-${reference.url}`} className="is-removed">삭제 · {reference.url}</li>
                  ))}
                </ul>
              )}
              {field === 'keywords' && (
                <div className="quality-panel__keywords">
                  {(suggestion.keywords || []).map((keyword) => (
                    <span key={keyword} className={(question.keywords || []).includes(keyword) ? '' : 'is-added'}>{keyword}</span>
                  ))}
                  {(question.keywords || []).filter((keyword) => !(suggestion.keywords || []).includes(keyword)).map((keyword) => (
                    <span key={`removed-${keyword}`} className="is-removed">{keyword}</span>
                  ))}
                </div>
              )}
              {field === 'domain' && (
                <p className="quality-panel__change-value">
                  <del>{domainName(question.domain)}</del> → <ins>{domainName(suggestion.domain)}</ins>
                </p>
              )}
              {field === 'difficulty' && (
                <p className="quality-panel__change-value">
                  <del>{question.difficulty ? DIFFICULTY_NAMES[question.difficulty] : '미지정'}</del> → <ins>{suggestion.difficulty ? DIFFICULTY_NAMES[suggestion.difficulty] : '미지정'}</ins>
                </p>
              )}
              {(field === 'question_text' || field === 'explanation' || field === 'reasoning') && (
                <DiffView before={(question[field] as string) || ''} after={(suggestion[field] as string) || ''} />
              )}
            </div>
          ))}
        </div>
      )}

      {(urlNotes.length > 0 || (review.url_checks || []).length > 0) && (
        <div className="quality-panel__urls">
          <strong>URL 확인</strong>
          <ul>
            {(review.url_checks || []).map((check) => (
              <li key={check.url}>
                <span className={`quality-panel__url-state ${check.ok ? 'is-ok' : check.unverified ? 'is-unknown' : 'is-bad'}`}>
                  {check.ok ? '열림' : check.unverified ? '확인 불가' : '열리지 않음'}
                </span>
                <a href={check.url} target="_blank" rel="noreferrer">{check.url}</a>
                {check.title && <span className="quality-panel__url-title">{check.title}</span>}
              </li>
            ))}
            {urlNotes.map((note, index) => <li key={`note-${index}`} className="quality-panel__reason">{note.reason}</li>)}
          </ul>
        </div>
      )}

      <footer className="quality-panel__actions">
        <p>정답 번호와 선택지 순서는 바뀌지 않습니다. 적용 전 내용은 수정 이력에 남습니다.</p>
        <div>
          <button type="button" className="admin-btn sm secondary" onClick={onDismiss} disabled={busy}>
            이상 없음 · 완료
          </button>
          {suggestedFields.length > 0 && (
            <button type="button" className="admin-btn sm primary" onClick={() => onApply([...selected])} disabled={busy || selected.size === 0}>
              {busy ? '처리 중…' : `수정안 적용하고 완료 (${selected.size})`}
            </button>
          )}
        </div>
      </footer>
    </section>
  );
};

export default QualityReviewPanel;
